<template>
    <div class="background">
        <div class="overlay"></div>
        <div class="container-fluid">
            <div class="col-lg-12 col-sm-12 login-container">
                <div class="col-lg-8 col-sm-8 app-title">
                    <div class="brand-panel">
                        <div class="logo"><img src="logo.png" alt="PlePower"></div>
                        <p class="brand-eyebrow">Account Recovery</p>
                        <h2>{{ heroTitle }}</h2>
                        <p class="brand-copy">{{ heroCopy }}</p>
                        <div class="brand-highlights">
                            <div class="brand-highlight">
                                <strong>Secure recovery</strong>
                                <span>OTP is sent to your registered email or phone number.</span>
                            </div>
                            <div class="brand-highlight">
                                <strong>Quick access</strong>
                                <span>Verify the code and create a new pin without leaving this page.</span>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="col-lg-4 col-sm-4 login-form mt-5">
                    <div class="login-form-inner">
                        <div class="form-heading">
                            <p class="form-eyebrow">Forgot Pin</p>
                            <h3>{{ formTitle }}</h3>
                            <span>{{ formCopy }}</span>
                        </div>

                        <div class="step-indicator">
                            <div class="step-pill" :class="{ active: currentStep >= 1 }">
                                <span>1</span>
                                <strong>Request OTP</strong>
                            </div>
                            <div class="step-pill" :class="{ active: currentStep >= 2 }">
                                <span>2</span>
                                <strong>Verify code</strong>
                            </div>
                            <div class="step-pill" :class="{ active: currentStep >= 3 }">
                                <span>3</span>
                                <strong>Create pin</strong>
                            </div>
                        </div>

                        <div v-if="currentStep === 1" class="step-panel">
                            <div class="inputs email-column">
                                <label for="forgot-email">Email address or Phone<span class="text-danger">*</span></label>
                                <input
                                    id="forgot-email"
                                    type="text"
                                    class="form-control inputs-field"
                                    placeholder="Enter your email address or phone"
                                    v-model.trim="email_address"
                                    autocomplete="username"
                                >
                            </div>

                            <div class="inputs helper-row">
                                <p class="forgot-password">
                                    <router-link to="/"><i class="fa fa-arrow-left"></i> Back to sign in</router-link>
                                </p>
                            </div>

                            <div class="inputs btn-padding">
                                <button
                                    id="submit4"
                                    class="btn btn-success btn-block"
                                    :disabled="isOtpRequestDisabled"
                                    @click="requestOtp"
                                    v-if="!sendingOtp"
                                >
                                    Send OTP
                                </button>
                                <button class="btn btn-success btn-block" type="button" disabled v-else>
                                    <span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                                </button>
                            </div>
                        </div>

                        <div v-if="currentStep === 2" class="step-panel">
                            <div class="verification-note">
                                <strong>OTP sent</strong>
                                <span>Enter the 4-digit code sent to {{ maskedRecipient }}.</span>
                            </div>

                            <div class="inputs1" @paste="handlePaste">
                                <input
                                    type="text"
                                    maxlength="1"
                                    inputmode="numeric"
                                    pattern="[0-9]*"
                                    autocomplete="one-time-code"
                                    class="form-control inputs-field1"
                                    v-model="verify_code1"
                                    @input="verify_code1 = sanitizeDigit(verify_code1, 1); moveToNext(1)"
                                    @keydown.backspace="moveToPrevious($event, 1)"
                                    ref="input1"
                                >
                                <input
                                    type="text"
                                    maxlength="1"
                                    inputmode="numeric"
                                    pattern="[0-9]*"
                                    autocomplete="one-time-code"
                                    class="form-control inputs-field1"
                                    v-model="verify_code2"
                                    @input="verify_code2 = sanitizeDigit(verify_code2, 1); moveToNext(2)"
                                    @keydown.backspace="moveToPrevious($event, 2)"
                                    ref="input2"
                                >
                                <input
                                    type="text"
                                    maxlength="1"
                                    inputmode="numeric"
                                    pattern="[0-9]*"
                                    autocomplete="one-time-code"
                                    class="form-control inputs-field1"
                                    v-model="verify_code3"
                                    @input="verify_code3 = sanitizeDigit(verify_code3, 1); moveToNext(3)"
                                    @keydown.backspace="moveToPrevious($event, 3)"
                                    ref="input3"
                                >
                                <input
                                    type="text"
                                    maxlength="1"
                                    inputmode="numeric"
                                    pattern="[0-9]*"
                                    autocomplete="one-time-code"
                                    class="form-control inputs-field1"
                                    v-model="verify_code4"
                                    @input="verify_code4 = sanitizeDigit(verify_code4, 1); moveToNext(4)"
                                    @keydown.backspace="moveToPrevious($event, 4)"
                                    ref="input4"
                                >
                            </div>

                            <div class="inputs helper-row">
                                <p class="forgot-password">
                                    <button class="link-button" type="button" @click="goBackToEmail">Change email or phone</button>
                                </p>
                            </div>

                            <div class="inputs btn-padding">
                                <button
                                    id="submit5"
                                    class="btn btn-success btn-block"
                                    :disabled="isOtpVerifyDisabled"
                                    @click="verifyOtp"
                                    v-if="!verifyingOtp"
                                >
                                    Verify OTP
                                </button>
                                <button class="btn btn-success btn-block" type="button" disabled v-else>
                                    <span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                                </button>

                                <button
                                    class="btn btn-outline-success btn-block resend-btn"
                                    type="button"
                                    :disabled="sendingOtp"
                                    @click="requestOtp"
                                >
                                    Resend OTP
                                </button>
                            </div>
                        </div>

                        <div v-if="currentStep === 3" class="step-panel">
                            <div class="verification-note">
                                <strong>Create new pin</strong>
                                <span>Your OTP has been verified. Choose a new 4-digit pin.</span>
                            </div>

                            <div class="inputs">
                                <label for="new-pin">New Pin<span class="text-danger">*</span></label>
                                <input
                                    id="new-pin"
                                    type="password"
                                    class="form-control inputs-field"
                                    placeholder="Enter 4-digit pin"
                                    v-model="password"
                                    maxlength="4"
                                    inputmode="numeric"
                                    pattern="[0-9]*"
                                    autocomplete="new-password"
                                    @input="password = sanitizeDigit(password, 4)"
                                >

                                <label for="confirm-pin">Confirm Pin<span class="text-danger">*</span></label>
                                <input
                                    id="confirm-pin"
                                    type="password"
                                    class="form-control inputs-field"
                                    placeholder="Confirm 4-digit pin"
                                    v-model="confirm_password"
                                    maxlength="4"
                                    inputmode="numeric"
                                    pattern="[0-9]*"
                                    autocomplete="new-password"
                                    @input="confirm_password = sanitizeDigit(confirm_password, 4)"
                                >
                            </div>

                            <div class="inputs helper-row">
                                <p class="forgot-password">
                                    <button class="link-button" type="button" @click="goBackToOtp">Back to OTP</button>
                                </p>
                            </div>

                            <div class="inputs btn-padding">
                                <button
                                    id="submit6"
                                    class="btn btn-success btn-block"
                                    :disabled="isPinSubmitDisabled"
                                    @click="createPin"
                                    v-if="!savingPin"
                                >
                                    Create Pin
                                </button>
                                <button class="btn btn-success btn-block" type="button" disabled v-else>
                                    <span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                                </button>
                            </div>
                        </div>
                    </div>
                    <p>&nbsp;</p>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import Vue from 'vue'
import VueCountryCode from "vue-country-code";
import swal from 'sweetalert2';

Vue.use(VueCountryCode);

export default {
    name: 'ForgotPassword',

    data() {
        return {
            appTitle: "PlePower",
            currentStep: 1,
            email_address: "",
            password: "",
            confirm_password: "",
            verify_code1: "",
            verify_code2: "",
            verify_code3: "",
            verify_code4: "",
            verifiedOtp: "",
            sendingOtp: false,
            verifyingOtp: false,
            savingPin: false,
        };
    },

    beforeMount() {
        document.title = this.appTitle;
        localStorage.removeItem('otp');
    },

    computed: {
        heroTitle() {
            if (this.currentStep === 1) {
                return "Reset your pin and get back into your account.";
            }

            if (this.currentStep === 2) {
                return "Enter the OTP we sent to your account.";
            }

            return "Create a new pin and secure your account.";
        },

        heroCopy() {
            if (this.currentStep === 1) {
                return "Enter the email address or phone number linked to your account and we will send you a one-time code to continue.";
            }

            if (this.currentStep === 2) {
                return "We have sent a 4-digit OTP. Enter it below to unlock the pin reset screen.";
            }

            return "Use the verified code to set a fresh 4-digit pin and complete recovery.";
        },

        formTitle() {
            if (this.currentStep === 1) {
                return "Recover your account";
            }

            if (this.currentStep === 2) {
                return "Verify your OTP";
            }

            return "Create your new pin";
        },

        formCopy() {
            if (this.currentStep === 1) {
                return "We'll send a one-time code to help you regain access.";
            }

            if (this.currentStep === 2) {
                return "Enter the code you received to continue.";
            }

            return "Set a new 4-digit pin to finish the recovery process.";
        },

        maskedRecipient() {
            if (!this.email_address) {
                return "your account";
            }

            const value = this.email_address.trim();

            if (value.includes('@')) {
                const [name, domain = ''] = value.split('@');
                const visibleName = name.slice(0, 2);
                return `${visibleName}${name.length > 2 ? '***' : ''}@${domain}`;
            }

            const visibleTail = value.slice(-4);
            return `${value.length > 4 ? '***' : ''}${visibleTail}`;
        },

        isOtpRequestDisabled() {
            return this.email_address.trim() === '';
        },

        isOtpVerifyDisabled() {
            return (
                this.verify_code1 === '' ||
                this.verify_code2 === '' ||
                this.verify_code3 === '' ||
                this.verify_code4 === ''
            );
        },

        isPinSubmitDisabled() {
            return (
                this.password === '' ||
                this.confirm_password === '' ||
                this.password.length !== 4 ||
                this.confirm_password.length !== 4
            );
        },
    },

    methods: {
        sanitizeDigit(value, maxLength) {
            return String(value || '').replace(/[^0-9]/g, '').slice(0, maxLength);
        },

        clearOtpFields() {
            this.verify_code1 = '';
            this.verify_code2 = '';
            this.verify_code3 = '';
            this.verify_code4 = '';
        },

        focusOtpInput(index) {
            this.$nextTick(() => {
                const ref = this.$refs[`input${index}`];
                if (ref && ref.focus) {
                    ref.focus();
                }
            });
        },

        moveToNext(index) {
            const currentValue = this[`verify_code${index}`];
            if (currentValue.length === 1) {
                const nextIndex = index + 1;
                if (nextIndex <= 4) {
                    this.focusOtpInput(nextIndex);
                }
            }
        },

        handlePaste(event) {
            const pasteData = event.clipboardData?.getData('text') || '';
            const digits = pasteData.replace(/[^0-9]/g, '').slice(0, 4);

            if (digits.length !== 4) {
                return;
            }

            event.preventDefault();
            this.verify_code1 = digits[0];
            this.verify_code2 = digits[1];
            this.verify_code3 = digits[2];
            this.verify_code4 = digits[3];
            this.focusOtpInput(4);
        },

        moveToPrevious(event, index) {
            if (this[`verify_code${index}`]) {
                this[`verify_code${index}`] = '';
                return;
            }

            const previousIndex = index - 1;
            if (previousIndex >= 1) {
                this[`verify_code${previousIndex}`] = '';
                this.focusOtpInput(previousIndex);
            }
        },

        async requestOtp() {
            if (!this.email_address.trim()) {
                swal.fire({
                    icon: 'warning',
                    title: 'Error...',
                    width: 450,
                    height: 4,
                    text: 'Please enter your email address or phone number',
                });
                return;
            }

            try {
                this.sendingOtp = true;

                const response = await this.$http.post('/forgot-password1', {
                    email_address: this.email_address.trim(),
                });

                this.sendingOtp = false;

                if (!response.data.success) {
                    swal.fire({
                        icon: "error",
                        title: "Error",
                        width: 450,
                        height: 5,
                        text: response.data.message,
                    });
                    return;
                }

                swal.fire({
                    icon: "success",
                    title: "Success",
                    width: 450,
                    height: 5,
                    text: response.data.message || 'OTP sent successfully',
                });

                this.currentStep = 2;
                this.clearOtpFields();
                return;
            } catch (err) {
                this.sendingOtp = false;
                swal.fire({
                    icon: "error",
                    title: "Error",
                    width: 450,
                    height: 5,
                    text: err?.response?.data?.message || err.message || 'Unable to send OTP',
                });
                console.log(err);
            }
        },

        goBackToEmail() {
            this.currentStep = 1;
            this.clearOtpFields();
            this.verifiedOtp = '';
            localStorage.removeItem('otp');
        },

        async verifyOtp() {
            if (this.isOtpVerifyDisabled) {
                swal.fire({
                    icon: 'warning',
                    title: 'Error...',
                    width: 450,
                    height: 4,
                    text: 'Please enter the 4-digit OTP',
                });
                return;
            }

            const verify_code = `${this.verify_code1}${this.verify_code2}${this.verify_code3}${this.verify_code4}`;

            try {
                this.verifyingOtp = true;

                const response = await this.$http.post('/verify-otp', {
                    passcode: verify_code,
                });

                this.verifyingOtp = false;

                if (!response.data.success) {
                    swal.fire({
                        icon: "error",
                        title: "Error",
                        width: 450,
                        height: 5,
                        text: response.data.message,
                    });
                    return;
                }

                this.verifiedOtp = verify_code;
                localStorage.setItem('otp', verify_code);
                this.currentStep = 3;

                swal.fire({
                    icon: "success",
                    title: "Verified",
                    width: 450,
                    height: 5,
                    text: response.data.message || 'OTP verified successfully',
                });
            } catch (err) {
                this.verifyingOtp = false;
                swal.fire({
                    icon: "error",
                    title: "Error",
                    width: 450,
                    height: 5,
                    text: err?.response?.data?.message || err.message || 'Unable to verify OTP',
                });
                console.log(err);
            }
        },

        goBackToOtp() {
            this.currentStep = 2;
            this.password = '';
            this.confirm_password = '';
        },

        async createPin() {
            if (!this.password) {
                swal.fire({
                    icon: 'warning',
                    title: 'Error...',
                    width: 450,
                    height: 4,
                    text: 'Please enter your new pin',
                });
                return;
            }

            if (!this.confirm_password) {
                swal.fire({
                    icon: 'warning',
                    title: 'Error...',
                    width: 450,
                    height: 4,
                    text: 'Please confirm your new pin',
                });
                return;
            }

            if (this.password !== this.confirm_password) {
                swal.fire({
                    icon: 'warning',
                    title: 'Error...',
                    width: 450,
                    height: 4,
                    text: 'Pin does not match!',
                });
                return;
            }

            const otp = this.verifiedOtp || localStorage.getItem('otp');

            if (!otp) {
                swal.fire({
                    icon: 'warning',
                    title: 'Error...',
                    width: 450,
                    height: 4,
                    text: 'Your OTP session has expired. Please request a new code.',
                });
                this.goBackToEmail();
                return;
            }

            try {
                this.savingPin = true;

                const response = await this.$http.post('/create-password', {
                    otp,
                    password: this.password,
                    confirm_password: this.confirm_password,
                });

                this.savingPin = false;

                if (!response.data.success) {
                    swal.fire({
                        icon: "error",
                        title: "Error",
                        width: 450,
                        height: 5,
                        text: response.data.message,
                    });
                    return;
                }

                localStorage.removeItem('otp');

                swal.fire({
                    icon: "success",
                    title: "Success",
                    width: 450,
                    height: 5,
                    text: response.data.message || 'Your pin has been reset successfully',
                });

                this.$router.push('/');
            } catch (err) {
                this.savingPin = false;
                swal.fire({
                    icon: "error",
                    title: "Error",
                    width: 450,
                    height: 5,
                    text: err?.response?.data?.message || err.message || 'Unable to create pin',
                });
                console.log(err);
            }
        },
    },
}
</script>

<style lang="scss" scoped>
@import url('https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;700&display=swap');

.background{
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    min-height: 100vh;
    position: relative;
    backdrop-filter: blur(16px);
    background: url(../assets/bg.jpg) no-repeat fixed center;
    background-size: cover;
}

.overlay {
    position: absolute;
    inset: 0;
    background: #012b20; /* Change the color and opacity here */
    opacity: 0.9;
}

.login-container{
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 2rem;
    min-height: calc(100vh - 90px);
    padding: 1rem 5% 3rem;
}

.app-title{
    display: flex;
    flex-direction: column;
    align-items:center;
    justify-content: center;
    font-family: Poppins;
    font-size: 30px;
    font-weight: bold;
    text-align: left;
    color: #fff;
    padding: 2rem 3rem 2rem 0;
    text-transform: capitalize;

    .brand-panel{
        display: flex;
        flex-direction: column;
        gap: 1rem;
        max-width: 620px;
    }

    .brand-eyebrow{
        margin: 0;
        font-size: 12px;
        font-weight: 700;
        text-transform: uppercase;
        letter-spacing: 0.12em;
        color: #bcd8c2;
    }

    .brand-copy{
        font-size: 16px;
        font-weight: 400;
        line-height: 1.8;
        color: rgba(255, 255, 255, 0.88);
        margin: 0;
    }

    .brand-highlights{
        display: flex;
        gap: 1rem;
        margin-top: 0.75rem;
    }

    .brand-highlight{
        display: flex;
        flex-direction: column;
        gap: 0.35rem;
        padding: 1rem 1.1rem;
        border-radius: 18px;
        background: rgba(255,255,255,0.12);
        border: 1px solid rgba(255,255,255,0.12);
        backdrop-filter: blur(8px);

        strong{
            font-size: 15px;
            color: #fff;
        }

        span{
            font-size: 13px;
            color: rgba(255,255,255,0.8);
        }
    }

    .logo{
        img{
            height: 74px;
            width: auto;
        }
    }

    h2{
        font-family: Poppins;
        font-size: 56px;
        line-height: 1.08;
        color: white;
        margin: 0;
    }
}

.login-form{
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
}

.login-form-inner{
    display: flex;
    flex-direction: column;
    background: rgba(255, 255, 255, 0.98);
    border-radius: 28px;
    padding: 1.6rem;
    width: 100%;
    box-shadow: 0 24px 60px rgba(0, 0, 0, 0.24);
    font-family: Poppins;
    font-size: 13px;
    font-weight: 500;
    color: #000 !important;
    border: 1px solid rgba(36, 86, 43, 0.08);
}

.form-heading{
    display: flex;
    flex-direction: column;
    gap: 0.35rem;
    margin-bottom: 0.8rem;

    h3{
        margin: 0;
        font-size: 28px;
        font-weight: 700;
        color: #17352b;
    }

    span{
        font-size: 13px;
        color: #5d7269;
        line-height: 1.7;
    }
}

.form-eyebrow{
    margin: 0;
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.12em;
    color: #659610;
}

.step-indicator{
    display: flex;
    gap: 0.65rem;
    flex-wrap: wrap;
    margin: 0.35rem 0 1rem;
}

.step-pill{
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.6rem 0.75rem;
    border-radius: 999px;
    background: #f3f7f4;
    color: #6d8077;
    border: 1px solid #dbe4de;
    font-size: 11px;

    span{
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 20px;
        height: 20px;
        border-radius: 50%;
        background: #dfe8e1;
        color: #24562B;
        font-weight: 700;
        font-size: 10px;
    }

    &.active{
        background: rgba(36, 86, 43, 0.08);
        border-color: rgba(36, 86, 43, 0.16);
        color: #24562B;

        span{
            background: #24562B;
            color: #fff;
        }
    }
}

.step-panel{
    display: flex;
    flex-direction: column;
}

.verification-note{
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
    padding: 0.9rem 1rem;
    border-radius: 16px;
    background: #f6faf7;
    border: 1px solid #e2ece5;
    margin-bottom: 0.9rem;

    strong{
        color: #17352b;
        font-size: 14px;
    }

    span{
        color: #60756c;
        line-height: 1.6;
        font-size: 13px;
    }
}

.inputs{
    display: flex;
    flex-direction: column;
    margin-top: 12px;
    border-radius: 3px;
    font-size:11px;
    font-weight: 400;
    gap: 0.5rem;

    .inputs-field{
        font-size: 13px;
        font-family: Poppins;
        height: 50px;
        border: 1px solid #dbe4de;
        border-radius: 14px;
        box-shadow: none;
    }
}

.inputs1{
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 0.65rem;
    margin-top: 12px;
}

.inputs-field1{
    height: 52px;
    text-align: center;
    font-size: 18px;
    font-weight: 700;
    border-radius: 14px;
    border: 1px solid #dbe4de;
    box-shadow: none;
}

.helper-row{
    margin-top: 16px;
}

.btn-padding{
    margin-top: 8px;
}

.forgot-password{
    margin: 0;

    a {
        color: #24562B;
        text-decoration: none;
        font-weight: 600;
    }
}

.link-button{
    border: 0;
    background: transparent;
    color: #24562B;
    font-weight: 600;
    padding: 0;
    text-align: left;
}

.form-control{
    border: 1px solid #dbe4de;
}

.btn-success{
    text-transform: uppercase;
    font-size: 13px;
    font-family: Poppins;
    background: #24562B;
    border: 0px;
    height: 50px;
    border-radius: 14px;
    font-weight: 700;
}

.btn-outline-success{
    text-transform: uppercase;
    font-size: 13px;
    font-family: Poppins;
    height: 50px;
    border-radius: 14px;
    font-weight: 700;
    border-color: #24562B;
    color: #24562B;
}

.resend-btn{
    margin-top: 0.35rem;
    background: transparent;
}

body, html {
    height: 100%;
    margin: 0;
}

@media screen and (max-width: 768px) {
    .background{
        font-family: Poppins !important;
        height: 100%;
        position: relative;
        backdrop-filter: blur(16px);
        background-size: cover;
    }

    .overlay {
        
    }

    .login-container{
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        gap: 1rem;
        padding: 1rem 1rem 2rem;
    }

    .app-title{
        font-size: 20px;
        font-weight: bold;
        color: #fff;
        padding: 0rem;
        text-align: center;
        width: 100%;

        .brand-panel{
            gap: 0.8rem;
        }

        .brand-highlights{
            flex-direction: column;
        }

        h2{
            font-size: 30px;
            text-align: center;
        }

        .brand-copy{
            font-size: 12px;
            text-align: center;
        }
    }

    .login-form{
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        width: 100%;
        height: 100%;
        padding-right: 0rem;
    }

    .login-form-inner{
        width: 100%;
        border-radius: 20px;
        padding: 1.25rem;
    }

    .inputs{
        .inputs-field{
            font-size: 12px;
            height: 46px;
        }
    }

    .inputs1{
        gap: 0.5rem;
    }

    .inputs-field1{
        height: 48px;
    }
}
</style>
